/**
 * Props:
 * - data: (array) Optional. Array of objects, each with { label: string, value: number }.
 *         Example: [{ label: 'Label 1', value: 0.1 }, { label: 'Label 2', value: 0.2 }, ...]
 *
 * - labels: (array) Optional. Array of strings. Used with "values" if "data" is not provided.
 * - values: (array) Optional. Array of numbers corresponding to each label.
 *
 * - width/height: (number) Optional. Width/height of the chart. If a number is provided, that value is used
 *                  for internal calculations. If omitted, the SVG defaults to "100%" width (responsive)
 *                  with an internal coordinate size of 500.
 *
 * - minHeight/minWidth/maxHeight/maxWidth: (number) Optional. Aditional size control props.
 *
 * ***To control size of the ring chart, either set a size on the chart itself or on the parent container.***
 *
 * - colors: (array) Optional. Array of color strings for the ring sections.
 *           If not provided, a default palette is used.
 *
 * - selected: (number) Optional. The index of the value to be emphasized upon component load. User can then hover to
 *              emphasize the other values. Else emphasizes the largest value. Falls back to index 0.
 *
 *
 * - showLegend: (bool) Optional. Defaults to false.
 *
 * - legendPlacement: (string: 'left' or 'right') Optional. Defaults to 'right'. If showLegend is set to true,
 *                    dictates which side of the ringchart the legend appears on.
 *
 * - legendAlignment: (string: 'start', 'center', or 'end') Optional. Defaults to 'start'. If showLegend is set to true,
 *                    dictates the alignment of the legend with the ring chart.
 *
 * - legendWidth: (number) Customize the width of the legend container.
 *
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Message } from 'semantic-ui-react';
import styled from 'styled-components';
import AnimatedArc from './AnimatedArc';
import { ringChartColors } from '../../utils/colors';

class RingChart extends Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedIndex: null,
    };
  }

  componentWillMount() {
    this.getIndex();
  }

  componentDidUpdate(prevProps) {
    const { data, values } = this.props;

    if (data !== prevProps.data || values !== prevProps.values) {
      this.getIndex();
    }
  }

  handleMouseOver(index) {
    this.setState({ selectedIndex: index });
  }

  getIndex() {
    const { data, values, selected } = this.props;
    let index = selected;
    if (selected === null) {
      if (data && data.length > 0) {
        index = data.reduce(
          (maxIndex, d, i, arr) =>
            d.value > arr[maxIndex].value ? i : maxIndex,
          0
        );
      } else if (values && values.length > 0) {
        index = values.reduce(
          (maxIndex, d, i, arr) => (d > arr[maxIndex] ? i : maxIndex),
          0
        );
      }
    }
    return this.setState({ selectedIndex: index || 0 });
  }

  getData() {
    const { data, labels, values } = this.props;
    if (Array.isArray(data) && data.length) {
      return data;
    }
    if (
      Array.isArray(labels) &&
      Array.isArray(values) &&
      labels.length > 0 &&
      values.length > 0 &&
      labels.length === values.length
    ) {
      return labels.map((label, index) => ({ label, value: values[index] }));
    }
    return [];
  }

  getOptimalFontSize(text, availableWidth, minFontSize, maxFontSize) {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    const measureLine = (fontSize, line) => {
      context.font = `${fontSize}px sans-serif`;
      return context.measureText(line).width;
    };
    let lines = [text];
    if (text.indexOf(' ') !== -1) {
      const words = text.split(' ');
      const mid = Math.floor(words.length / 2);
      lines = [words.slice(0, mid).join(' '), words.slice(mid).join(' ')];
    }
    let low = minFontSize;
    let high = maxFontSize;
    let optimal = low;
    while (high - low > 0.1) {
      const mid = (low + high) / 2;
      const widths = lines.map((line) => measureLine(mid, line));
      const maxWidth = Math.max(...widths);
      if (maxWidth <= availableWidth) {
        optimal = mid;
        low = mid;
      } else {
        high = mid;
      }
    }
    return { fontSize: Math.round(optimal), lines };
  }

  hasInvalidValue = (data) =>
    data.filter((item) => item.value == null || item.value < 0).length !== 0;

  wrapText(text) {
    if (text.indexOf(' ') === -1) return [text];
    const words = text.split(' ');
    const mid = Math.floor(words.length / 2);
    return [words.slice(0, mid).join(' '), words.slice(mid).join(' ')];
  }

  renderLegend(data, colors) {
    return (
      <div className="legend">
        {data.map((item, i) => (
          <div key={i} className="legend-item">
            <Dot color={colors[i]} />
            <div>{item.label}</div>
          </div>
        ))}
      </div>
    );
  }

  render() {
    const {
      height,
      width,
      colors,
      minHeight,
      maxHeight,
      minWidth,
      maxWidth,
      showLegend,
      legendPlacement,
      legendAlignment,
      legendWidth,
    } = this.props;
    const { selectedIndex } = this.state;

    let actualWidth = typeof width === 'number' ? width : 500;
    let actualHeight = typeof height === 'number' ? height : 500;

    if (typeof minWidth === 'number') {
      actualWidth = Math.max(actualWidth, minWidth);
    }
    if (typeof maxWidth === 'number') {
      actualWidth = Math.min(actualWidth, maxWidth);
    }
    if (typeof minHeight === 'number') {
      actualHeight = Math.max(actualHeight, minHeight);
    }
    if (typeof maxHeight === 'number') {
      actualHeight = Math.min(actualHeight, maxHeight);
    }

    const svgWidth =
      actualWidth === 500
        ? '100%'
        : actualWidth - (showLegend ? legendWidth : 0);
    const svgHeight =
      actualHeight === 500
        ? '100%'
        : actualHeight - (showLegend ? legendWidth : 0);

    const data = this.getData();
    if (this.hasInvalidValue(data)) {
      return (
        <Message visible warning style={{ marginTop: '2rem' }}>
          Chart cannot be displayed as data contains null or negative values.
        </Message>
      );
    }
    if (data.length === 0) {
      return (
        <Message visible warning style={{ marginTop: '2rem' }}>
          Chart data is unavailable.
        </Message>
      );
    }

    const padding = 10;
    const cx = actualWidth / 2;
    const cy = actualHeight / 2;
    const maxAvailableRadius =
      (Math.min(actualWidth, actualHeight) - padding) / 2;

    const thicknessFactor = 0.15;
    const selectedExtensionFactor = 1.5;
    const expansionFactor =
      1 - thicknessFactor + selectedExtensionFactor * thicknessFactor;
    const baseOuterRadius = maxAvailableRadius / expansionFactor;
    const ringThickness = baseOuterRadius * thicknessFactor;
    const innerRadius = baseOuterRadius - ringThickness;

    const total = data.reduce((sum, d) => sum + d.value, 0);
    let currentAngle = -Math.PI / 2;
    const arcData = data.map((d) => {
      const angle = (d.value / total) * 2 * Math.PI;
      const startAngle = currentAngle;
      const endAngle = currentAngle + angle;
      currentAngle = endAngle;
      return { startAngle, endAngle, data: d };
    });

    const colorPalette = colors && colors.length > 0 ? colors : ringChartColors;

    const innerDiameter = innerRadius * 2;
    const availableTextWidth = innerDiameter * 0.8;

    const percentageValue = (data[selectedIndex].value * 100).toFixed(2);
    const percentageText =
      percentageValue === '100.00' ? '100%' : `${percentageValue}%`;

    const sampleLongPercentage = '10.11%';
    const percentageOptimal = this.getOptimalFontSize(
      sampleLongPercentage,
      availableTextWidth,
      20,
      200
    );
    const percentageFontSize = percentageOptimal.fontSize;

    const longestLabel = data.reduce(
      (acc, d) => (d.label.length > acc.length ? d.label : acc),
      ''
    );
    const maxLabelFontSize = percentageFontSize / 3;
    const labelFontResult = this.getOptimalFontSize(
      longestLabel,
      availableTextWidth,
      12,
      maxLabelFontSize
    );
    const labelFontSize = labelFontResult.fontSize;

    const selectedLabel = data[selectedIndex].label || '';
    const selectedLabelLines = this.wrapText(selectedLabel);

    return (
      <Container align={legendAlignment} legendWidth={legendWidth}>
        {showLegend &&
          legendPlacement === 'left' &&
          this.renderLegend(data, colorPalette)}
        <svg
          width={svgWidth}
          height={svgHeight}
          viewBox={`0 0 ${actualWidth} ${actualHeight}`}
        >
          {arcData.map((arc, i) => {
            const targetOuterRadius =
              selectedIndex === i
                ? innerRadius + ringThickness * selectedExtensionFactor
                : baseOuterRadius;
            return (
              <AnimatedArc
                key={i}
                cx={cx}
                cy={cy}
                innerRadius={innerRadius}
                startAngle={arc.startAngle}
                endAngle={arc.endAngle}
                targetOuterRadius={targetOuterRadius}
                fill={colorPalette[i % colorPalette.length]}
                onMouseOver={() => this.handleMouseOver(i)}
                onFocus={() => this.handleMouseOver(i)}
              />
            );
          })}
          <text textAnchor="middle" fill="#333">
            <tspan x={cx} y={cy + 20} fontSize={percentageFontSize}>
              {percentageText}
            </tspan>
            {selectedLabelLines.map((line, index) => (
              <tspan
                key={index}
                x={cx}
                dy={index === 0 ? labelFontSize + 25 : labelFontSize + 10}
                fontSize={labelFontSize}
              >
                {line}
              </tspan>
            ))}
          </text>
        </svg>
        {showLegend &&
          legendPlacement === 'right' &&
          this.renderLegend(data, colorPalette)}
      </Container>
    );
  }
}

RingChart.propTypes = {
  height: PropTypes.number,
  width: PropTypes.number,
  maxHeight: PropTypes.number,
  maxWidth: PropTypes.number,
  minHeight: PropTypes.number,
  minWidth: PropTypes.number,
  colors: PropTypes.arrayOf(PropTypes.string),
  data: PropTypes.array,
  labels: PropTypes.array,
  values: PropTypes.array,
  selected: PropTypes.number,
  showLegend: PropTypes.bool,
  legendPlacement: PropTypes.string,
  legendAlignment: PropTypes.string,
  legendWidth: PropTypes.number,
};

RingChart.defaultProps = {
  height: null,
  width: null,
  maxHeight: null,
  maxWidth: null,
  minHeight: null,
  minWidth: null,
  colors: [],
  data: [],
  labels: [],
  values: [],
  selected: null,
  showLegend: false,
  legendPlacement: 'right',
  legendAlignment: 'start',
  legendWidth: 150,
};

export default RingChart;

const Container = styled.div`
  display: flex;
  flex-diretion: row;
  column-gap: 1rem;
  align-items: ${(props) => props.align};

  .legend {
    display: flex;
    flex-direction: column;
    row-gap: 5px;
    max-width: ${(props) => `${props.legendWidth}px`};
    min-width: ${(props) => `${props.legendWidth}px`};
    width: ${(props) => `${props.legendWidth}px`};
    .legend-item {
      display: flex;
      flex-direction: row;
      align-items: start;
      column-gap: 5px;
      font-size: 15px;
    }
  }
`;

const Dot = styled.div`
  height: 15px;
  width: 15px;
  min-width: 15px;
  background-color: ${(props) => props.color};
  border-radius: 50%;
  margin-top: 3px;
`;
